import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, FileText, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PatientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState("A");
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    axios.get(`${API}/patient-portal/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data?.signed_at) setSigned(true);
        if (r.data?.patient_first_name) setName(r.data.patient_first_name);
      })
      .catch(() => toast.error("Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  const sign = async () => {
    if (!name.trim()) { toast.error("Please type your full name"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/patient-portal/${token}/sign`, {
        full_name: name, accepted_option: choice, consent_text_version: "v1-2026-02",
      });
      setSigned(true);
      toast.success("Thank you — your plan acceptance is recorded.");
    } catch (e) {
      toast.error("Could not record signature");
    } finally { setSigning(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#050B14] text-[#E8E2D6]"><Loader2 className="animate-spin" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#050B14] text-[#E8E2D6]">Link not found.</div>;

  const isES = data.language === "es";
  const t = (en, es) => isES ? es : en;
  const options = data.plan?.options || [];

  return (
    <div className="min-h-screen bg-[#050B14] text-[#E8E2D6] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <span className="font-serif text-2xl">Prostho<span className="text-[#D4AF37]">AI</span></span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#857C6B] ml-2">{t("Patient Portal", "Portal del Paciente")}</span>
        </div>

        <h1 className="font-serif text-3xl" data-testid="portal-title">
          {t(`Hi ${name || "there"} — your treatment plan`, `Hola ${name || ""} — su plan de tratamiento`)}
        </h1>
        <p className="text-[#857C6B] text-sm mt-2 max-w-2xl">
          {t("Prepared by Dr. Ronik Seecharan DMD, Prosthodontist. Please review your options below and select the one you would like to move forward with.",
             "Preparado por Dr. Ronik Seecharan DMD, Prostodoncista. Por favor revise sus opciones a continuación y seleccione la que desea seguir.")}
        </p>

        {data.presentation?.sections && (
          <section className="mt-6 space-y-3" data-testid="portal-presentation">
            {data.presentation.sections.map((s, i) => (
              <details key={i} className="bg-[#0D1524] border border-[#1F2A40] rounded p-4">
                <summary className="cursor-pointer font-serif text-lg">{s.n}. {s.title}</summary>
                <pre className="mt-2 text-sm whitespace-pre-wrap font-sans leading-relaxed text-[#D8CFB7]">{s.body_markdown}</pre>
              </details>
            ))}
          </section>
        )}

        <h2 className="font-serif text-2xl mt-10">{t("Choose your option", "Elija su opción")}</h2>
        <div className="mt-3 grid sm:grid-cols-3 gap-3" data-testid="portal-options">
          {options.map((o, i) => {
            const letter = (o.label || "").trim()[0];
            const selected = choice === letter;
            return (
              <button key={i} onClick={() => setChoice(letter)} data-testid={`portal-option-${letter}`}
                className={`text-left p-4 rounded border transition ${selected ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#1F2A40] bg-[#0D1524] hover:border-[#857C6B]"}`}>
                <div className="font-serif text-xl text-[#D4AF37]">{o.label}</div>
                <p className="text-xs text-[#857C6B] mt-1">{o.summary}</p>
                <div className="mt-2 text-xs text-[#857C6B]">{t("Total", "Total")}: ${(o.total_fee_usd || 0).toLocaleString()}</div>
                <div className="text-sm text-[#E8E2D6] font-semibold">{t("You pay", "Usted paga")}: ${(o.estimated_patient_usd || 0).toLocaleString()}</div>
              </button>
            );
          })}
        </div>

        <h2 className="font-serif text-2xl mt-10">{t("Digital signature & acceptance", "Firma digital y aceptación")}</h2>
        <div className="mt-3 bg-[#0D1524] border border-[#1F2A40] rounded p-5">
          <p className="text-xs text-[#857C6B] leading-relaxed">
            {data.presentation?.consent_summary || t(
              "By typing my name below, I acknowledge that I have reviewed my treatment plan, understand the recommended care, fees, and alternatives, and accept the selected option. This is not a legal substitute for in-office consent which will be reviewed at my next visit.",
              "Al escribir mi nombre a continuación, reconozco que he revisado mi plan de tratamiento, entiendo el cuidado recomendado, los costos y las alternativas, y acepto la opción seleccionada."
            )}
          </p>
          {signed ? (
            <div className="mt-4 flex items-center gap-2 text-emerald-400 font-semibold" data-testid="portal-signed">
              <CheckCircle2 size={18} /> {t("Signed & accepted", "Firmado y aceptado")} — {t("Option", "Opción")} {data.accepted_option || choice}
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Type your full legal name", "Escriba su nombre legal completo")}
                data-testid="portal-name-input"
                className="w-full bg-[#050B14] border border-[#1F2A40] rounded p-2.5 text-sm text-[#E8E2D6] focus:outline-none focus:border-[#D4AF37]" />
              <button onClick={sign} disabled={signing} data-testid="portal-sign"
                className="flex items-center gap-2 bg-[#D4AF37] text-[#050B14] font-semibold px-5 py-2.5 rounded hover:opacity-90 disabled:opacity-60">
                {signing ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {t("Sign & accept option", "Firmar y aceptar opción")} {choice}
              </button>
            </div>
          )}
        </div>

        {data.education?.topics && (
          <section className="mt-10" data-testid="portal-education">
            <h2 className="font-serif text-2xl mb-3">{t("Patient education", "Educación del paciente")}</h2>
            {data.education.topics.map((tp, i) => (
              <details key={i} className="bg-[#0D1524] border border-[#1F2A40] rounded p-4 mb-2">
                <summary className="cursor-pointer font-serif text-lg">{tp.title_friendly}</summary>
                <pre className="mt-2 text-sm whitespace-pre-wrap font-sans leading-relaxed text-[#D8CFB7]">{tp.summary_markdown}</pre>
              </details>
            ))}
          </section>
        )}

        <footer className="mt-12 pt-6 border-t border-[#1F2A40] text-xs text-[#857C6B] text-center">
          Dr. Ronik Seecharan DMD · Prosthodontist · Boca Raton, FL · Patent Pending US 64/062,147
        </footer>
      </div>
    </div>
  );
}
